import type { RaceConfig } from '../../types/config.ts';
import type { Snapshot } from '../../types/snapshots.ts';
import { candidateColor } from '../../lib/colors.ts';

// Per-candidate line style so identity isn't carried by hue alone — the dash
// pattern mirrors each candidate's shape token.
const DASH: Record<string, string> = {
  solid: '',
  diagonal: '6 3',
  dot: '1.5 4',
  stripe: '8 3 2 3',
  grid: '4 3',
  ring: '2 4',
};

/** Vote share over time, one line per candidate. Pure SVG, aria-hidden — the
 *  accessible equivalent is a ChartTable rendered by the parent. */
export function TrendLines({
  race,
  snapshots,
  resolved,
  width = 360,
  height = 96,
}: {
  race: RaceConfig;
  snapshots: Snapshot[];
  resolved: 'dark' | 'light';
  width?: number;
  height?: number;
}) {
  const n = snapshots.length;
  const series = race.candidates.map((cand) => ({
    cand,
    pts: snapshots.map((s) => s.candidates.find((c) => c.id === cand.id)?.percent ?? null),
  }));

  const allVals = series.flatMap((s) => s.pts).filter((v): v is number => v != null);
  if (n < 2 || allVals.length < 2) {
    return (
      <p className="muted sparkline__empty">
        Collecting trend — a point is added on each refresh with new data.
      </p>
    );
  }

  const top = Math.max(10, Math.ceil(Math.max(...allVals) / 10) * 10);
  const pad = 6;
  const x = (i: number) => pad + (i / (n - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - (v / top) * (height - pad * 2);

  const lines = series.map(({ cand, pts }) => {
    let d = '';
    let started = false;
    let lastI = -1;
    pts.forEach((v, i) => {
      if (v == null) return;
      d += `${started ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
      started = true;
      lastI = i;
    });
    const lastV = lastI >= 0 ? pts[lastI] : null;
    return {
      id: cand.id,
      color: candidateColor(cand, resolved),
      dash: DASH[cand.pattern] ?? '',
      d: d.trim(),
      hasLine: started,
      lastX: lastI >= 0 ? x(lastI) : 0,
      lastY: lastV != null ? y(lastV) : 0,
    };
  });

  return (
    <svg
      className="trendlines"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      {lines.map((l) =>
        l.hasLine ? (
          <path
            key={l.id}
            d={l.d}
            fill="none"
            stroke={l.color}
            strokeWidth={2}
            strokeDasharray={l.dash || undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ) : null,
      )}
      {lines.map((l) =>
        l.hasLine ? <circle key={`${l.id}-dot`} cx={l.lastX} cy={l.lastY} r={2.8} fill={l.color} /> : null,
      )}
    </svg>
  );
}
